import { createPayment } from "@/apis/esendaV2/truelayer";
import { computed, onScopeDispose, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { configSentryError } from "@/helpers/sentry";
import { isPartnerLink } from "@/helpers/organisation";
import { useAlert } from "@esenda/core-ui/composables";
import { createSharedComposable } from "@vueuse/core";
import {
    usePaymentsStore,
    useAdyenStore,
    useLloydsStore,
    useCheckoutStore,
    useIFXStore,
    useVoltStore,
} from "@/plugins/store";
import {
    PaymentMethods,
    PaymentMethodType,
    BankTransferProviders,
} from "@esenda/core-ui/constants";
import { isWalletOnlyMethod } from "@/helpers/walletSupport";

export const useSubmitPaymentBase = () => {
    const { showAlert } = useAlert();

    const paymentsStore = usePaymentsStore();
    const adyenStore = useAdyenStore();
    const lloydsStore = useLloydsStore();
    const checkoutStore = useCheckoutStore();
    const ifxStore = useIFXStore();
    const voltStore = useVoltStore();

    const selectedPaymentMethod = ref<PaymentMethodType>();
    const selectedCardProvider = ref<string>();

    // Is 'standard' payment submitted i.e. non-wallet
    const isEsendaPaymentSubmitted = ref(false);

    // Instant
    const isInstantPayButtonLoading = ref(false);
    const invalidInstantNameForm = ref(true);

    const instantBankInfo = ref({
        showPayerInformation: false,
        selectedBank: { providerId: "" },
    });

    const isInstantBankSelected = computed(
        () => instantBankInfo.value.selectedBank.providerId !== "",
    );

    const isInstantPayButtonDisabled = computed(
        () => !isInstantBankSelected.value || invalidInstantNameForm.value,
    );

    // Adyen
    const adyenFormInfo = ref({
        buttonLoading: false,
        buttonDisabled: false,
        buttonVisible: true,
    });
    const isAdyenFormLoaded = ref(false);
    const invalidAdyenAddressForm = ref(true);
    const adyenSubmitFn = ref<(() => void) | null>(null);

    const isAdyenButtonLoading = computed(
        () => adyenFormInfo.value.buttonLoading,
    );

    const isAdyenButtonVisible = computed(
        () => adyenFormInfo.value.buttonVisible,
    );

    const isAdyenButtonDisabled = computed(() => {
        return (
            adyenStore.isPaymentActionInProgress ||
            !isAdyenFormLoaded.value ||
            adyenFormInfo.value.buttonLoading ||
            adyenFormInfo.value.buttonDisabled ||
            invalidAdyenAddressForm.value
        );
    });

    const adyenButtonTooltipText = computed(() =>
        invalidAdyenAddressForm.value || adyenFormInfo.value.buttonDisabled
            ? "alerts.validationError"
            : "default.empty",
    );

    // Lloyds
    const lloydsFormInfo = ref({
        buttonLoading: false,
        buttonDisabled: false,
        buttonVisible: true,
    });
    const isLloydsFormLoaded = ref(false);
    const invalidLloydsAddressForm = ref(true);
    const isLloydsPaymentSubmitted = ref(false);
    const lloydsFormInstance = ref<{
        triggerPaymentSubmit: () => Promise<string>;
        onPreFlowError: (cb: () => void) => void;
    } | null>(null);

    // Note: isLloydsFormLoaded is set by the Lloyds inline steps component once PaymentJS finishes initialising.

    const isLloydsButtonLoading = computed(
        () => lloydsFormInfo.value.buttonLoading,
    );

    const isLloydsButtonVisible = computed(
        () => lloydsFormInfo.value.buttonVisible,
    );

    const isLloydsButtonDisabled = computed(
        () =>
            !isLloydsFormLoaded.value ||
            lloydsFormInfo.value.buttonLoading ||
            lloydsFormInfo.value.buttonDisabled ||
            invalidLloydsAddressForm.value ||
            lloydsStore.isPaymentActionInProgress,
    );

    const lloydsButtonTooltipText = computed(() =>
        invalidLloydsAddressForm.value || lloydsFormInfo.value.buttonDisabled
            ? "alerts.validationError"
            : "default.empty",
    );

    // IFX bank transfer
    const isIfxBankDetailsLoaded = ref(false);
    const isIfxQuoteExpired = ref(false);
    const showIfxPayLater = ref(false);
    const isIfxPayLaterConfirm = ref(false);

    const isIfxPayButtonDisabled = computed(
        () => isIfxQuoteExpired.value || !isIfxBankDetailsLoaded.value,
    );
    const isIfxPayButtonVisible = computed(() => isIfxBankDetailsLoaded.value);

    // Shared
    const isPayButtonLoading = computed(() => {
        if (selectedPaymentMethod.value === PaymentMethodType.INSTANT) {
            return isInstantPayButtonLoading.value;
        }

        if (selectedPaymentMethod.value === PaymentMethodType.GLOBAL) {
            return false;
        }

        return selectedCardProvider.value === PaymentMethods.LLOYDS
            ? isLloydsButtonLoading.value
            : isAdyenButtonLoading.value;
    });

    const isPayButtonDisabled = computed(() => {
        if (selectedPaymentMethod.value === PaymentMethodType.INSTANT) {
            return isInstantPayButtonDisabled.value;
        }

        if (selectedPaymentMethod.value === PaymentMethodType.GLOBAL) {
            return isIfxPayButtonDisabled.value;
        }

        return selectedCardProvider.value === PaymentMethods.LLOYDS
            ? isLloydsButtonDisabled.value
            : isAdyenButtonDisabled.value;
    });

    const isCardPaymentActionInProgress = computed(
        () =>
            lloydsStore.isPaymentActionInProgress ||
            (adyenStore.isPaymentActionInProgress &&
                !adyenStore.isWeChatActionInProgress),
    );

    // Selecting a wallet-only method (e.g. WeChat Pay / Alipay) submits the
    // payment through the wallet button itself, so the generic Pay button
    // should not be shown for these methods.
    const isSelectedWalletOnlyMethod = computed(() =>
        isWalletOnlyMethod(paymentsStore.selectedProviderDetails),
    );
    if (!selectedPaymentMethod.value && paymentsStore.selectedProviderDetails) {
        const method = paymentsStore.selectedProviderDetails
            .paymentMethod as PaymentMethods;

        if (method === PaymentMethods.TRUELAYER) {
            selectedPaymentMethod.value = PaymentMethodType.INSTANT;
        } else if (BankTransferProviders.includes(method)) {
            selectedPaymentMethod.value = PaymentMethodType.GLOBAL;
        } else {
            selectedPaymentMethod.value = PaymentMethodType.CARD;
            selectedCardProvider.value = method;
        }
    }

    // Instant
    const resetInstantState = (): void => {
        isInstantPayButtonLoading.value = false;
        invalidInstantNameForm.value = true;
        instantBankInfo.value = {
            showPayerInformation: false,
            selectedBank: { providerId: "" },
        };
    };

    const submitInstantPayment = async () => {
        if (isInstantBankSelected.value) {
            try {
                isInstantPayButtonLoading.value = true;
                await createPayment(instantBankInfo.value.selectedBank);
            } finally {
                isInstantPayButtonLoading.value = false;
            }
        }
    };

    const onSubmitInstantPayment = async () => {
        checkoutStore.setTriggerPayerInformationValidation(true);
    };

    const onInstantPayerValidationComplete = async (
        isValid: boolean,
    ): Promise<void> => {
        if (isValid) {
            await submitInstantPayment();
        }
    };

    const handleInvalidInstantNameState = (isInvalid: boolean): void => {
        invalidInstantNameForm.value = isInvalid;
    };

    // Adyen
    const resetAdyenState = (): void => {
        adyenFormInfo.value = {
            buttonLoading: false,
            buttonDisabled: false,
            buttonVisible: true,
        };
        isAdyenFormLoaded.value = false;
        invalidAdyenAddressForm.value = true;
        adyenSubmitFn.value = null;
    };

    const registerAdyenSubmitFn = (fn: (() => void) | null): void => {
        adyenSubmitFn.value = fn;
    };

    const submitAdyenForm = () => {
        try {
            adyenSubmitFn.value?.();
        } catch (error) {
            configSentryError(
                "inlinePaymentSteps-Adyen",
                "pay",
                `Error submitting Adyen form: ${JSON.stringify(error, null, 2)}`,
            );
            paymentsStore.setIsPaymentInProgress(false);
            showAlert("alerts.unexpectedErrorWithRetryAction");
        }
    };

    // Only clears in-progress state on failed validation; submission itself
    // happens from a second button click, once isAdyenButtonDisabled is false
    const onAdyenPayerValidationComplete = (isValid: boolean) => {
        if (!isValid) {
            paymentsStore.setIsPaymentInProgress(false);
        }
    };

    const onSubmitAdyenPayment = () => {
        if (adyenStore.isPaymentActionInProgress) {
            submitAdyenForm();
            return;
        }
        if (invalidAdyenAddressForm.value) {
            paymentsStore.setIsPaymentInProgress(true);
            checkoutStore.setTriggerPayerInformationValidation(true);
            return;
        }
        paymentsStore.setIsPaymentInProgress(true);
        submitAdyenForm();
    };

    const handleInvalidAdyenAddressState = (isInvalid: boolean): void => {
        invalidAdyenAddressForm.value = isInvalid;
    };

    // Lloyds
    const resetLloydsState = (): void => {
        lloydsFormInfo.value = {
            buttonLoading: false,
            buttonDisabled: false,
            buttonVisible: true,
        };
        isLloydsFormLoaded.value = false;
        invalidLloydsAddressForm.value = true;
        isLloydsPaymentSubmitted.value = false;
        lloydsFormInstance.value = null;
    };

    const registerLloydsFormInstance = (
        instance: typeof lloydsFormInstance.value,
    ): void => {
        lloydsFormInstance.value = instance;
    };

    const submitLloydsForm = async (): Promise<void> => {
        if (!lloydsFormInstance.value) {
            showAlert("alerts.unexpectedErrorWithRetryAction");
            paymentsStore.setIsPaymentInProgress(false);
            return;
        }
        try {
            lloydsFormInfo.value.buttonDisabled = true;
            lloydsFormInfo.value.buttonLoading = true;

            lloydsFormInstance.value.onPreFlowError(() => {
                showAlert("alerts.unexpectedErrorWithRetryAction");
                lloydsFormInfo.value.buttonDisabled = false;
                lloydsFormInfo.value.buttonLoading = false;
                lloydsStore.setIsPaymentActionInProgress(false);
                paymentsStore.setIsPaymentInProgress(false);
            });

            await lloydsFormInstance.value.triggerPaymentSubmit();
            isLloydsPaymentSubmitted.value = true;
            lloydsStore.setIsPaymentActionInProgress(true);
        } catch (error) {
            configSentryError(
                "inlinePaymentSteps-Lloyds",
                "pay",
                `Error submitting Lloyds form: ${JSON.stringify(error, null, 2)}`,
            );
            lloydsStore.setIsPaymentActionInProgress(false);
            paymentsStore.setIsPaymentInProgress(false);
        } finally {
            lloydsFormInfo.value.buttonDisabled = false;
            lloydsFormInfo.value.buttonLoading = false;
        }
    };

    const onLloydsPayerValidationComplete = (isValid: boolean) => {
        if (!isValid) {
            paymentsStore.setIsPaymentInProgress(false);
        }
    };

    const onSubmitLloydsPayment = () => {
        if (invalidLloydsAddressForm.value) {
            paymentsStore.setIsPaymentInProgress(true);
            checkoutStore.setTriggerPayerInformationValidation(true);
            return;
        }

        paymentsStore.setIsPaymentInProgress(true);
        submitLloydsForm();
    };

    const handleInvalidLloydsAddressState = (isInvalid: boolean): void => {
        invalidLloydsAddressForm.value = isInvalid;
    };

    // IFX bank transfer
    const route = useRoute();
    const router = useRouter();

    const resetIfxState = (): void => {
        isIfxBankDetailsLoaded.value = false;
        isIfxQuoteExpired.value = false;
        showIfxPayLater.value = false;
        isIfxPayLaterConfirm.value = false;
    };

    const handleIfxBankDetailsLoaded = (): void => {
        isIfxBankDetailsLoaded.value = true;
    };

    const handleIfxQuoteExpiredState = (isExpired: boolean): void => {
        isIfxQuoteExpired.value = isExpired;
    };

    const isIfxInfoStep = computed(
        () =>
            selectedPaymentMethod.value === PaymentMethodType.GLOBAL &&
            (!route.hash || route.hash === "#info"),
    );

    const isIfxTransferStep = computed(
        () =>
            selectedPaymentMethod.value === PaymentMethodType.GLOBAL &&
            route.hash === "#transfer",
    );

    const isIfxConfirmStep = computed(
        () =>
            selectedPaymentMethod.value === PaymentMethodType.GLOBAL &&
            route.hash === "#confirm",
    );

    const isIfxSelected = computed(
        () =>
            paymentsStore.selectedProviderDetails?.paymentMethod ===
            PaymentMethods.IFX,
    );

    const isIfxFx = computed(
        () => paymentsStore.selectedProviderDetails?.isFxMethod ?? false,
    );

    const goToIfxTransferStep = (): void => {
        showIfxPayLater.value = false;
        isIfxPayLaterConfirm.value = false;
        router.replace({
            path: route.path,
            query: route.query,
            hash: "#transfer",
        });
    };

    const goToIfxConfirmStep = (): void => {
        isIfxPayLaterConfirm.value = true;
        router.replace({
            path: route.path,
            query: route.query,
            hash: "#confirm",
        });
    };

    const onIfxPayLater = (): void => {
        if (isIfxFx.value) {
            showIfxPayLater.value = true;
        } else {
            goToIfxConfirmStep();
        }
    };

    const onSubmitIfxPayment = (): void => {
        router.replace({
            path: route.path,
            query: route.query,
            hash: "#confirm",
        });
    };

    // Volt bank transfer
    const isVoltPayLaterConfirm = ref(false);
    const isVoltPayerFormInvalid = ref(true);

    const isVoltSelected = computed(
        () =>
            paymentsStore.selectedProviderDetails?.paymentMethod ===
            PaymentMethods.VOLT,
    );

    const isVoltConfirmStep = computed(
        () => isVoltSelected.value && route.hash === "#confirm",
    );

    const resetVoltState = (): void => {
        isVoltPayLaterConfirm.value = false;
        isVoltPayerFormInvalid.value = true;
    };

    const goToVoltDetailsStep = (): void => {
        isVoltPayLaterConfirm.value = false;
        router.replace({
            path: route.path,
            query: route.query,
            hash: "#details",
        });
    };

    const goToVoltPayerStep = (): void => {
        router.replace({
            path: route.path,
            query: route.query,
            hash: "#payer",
        });
    };

    const onVoltPaymentMade = (): void => {
        isVoltPayLaterConfirm.value = false;
        router.replace({
            path: route.path,
            query: route.query,
            hash: "#confirm",
        });
    };

    const onVoltPayLater = (): void => {
        isVoltPayLaterConfirm.value = true;
        router.replace({
            path: route.path,
            query: route.query,
            hash: "#confirm",
        });
    };

    // Partner-link redirect (bank transfer confirm steps only, shared across desktop/mobile UI)
    const redirectCountdown = ref(15);
    const showRedirectCountdown = ref(false);
    let redirectInterval: ReturnType<typeof setInterval> | null = null;

    const partnerReturnUrl = computed(() => {
        if (isIfxSelected.value && route.hash === "#confirm") {
            return ifxStore.ifxBankTransferInformation?.returnUrl ?? null;
        }
        if (isVoltConfirmStep.value) {
            return voltStore.voltPaymentInformation?.returnUrl ?? null;
        }
        return null;
    });

    const isBankTransferPaid = computed(() => {
        if (isIfxSelected.value && route.hash === "#confirm") {
            return !isIfxPayLaterConfirm.value;
        }
        if (isVoltConfirmStep.value) return !isVoltPayLaterConfirm.value;
        return false;
    });

    const clearRedirectCountdown = (): void => {
        if (redirectInterval) {
            clearInterval(redirectInterval);
            redirectInterval = null;
        }
        showRedirectCountdown.value = false;
    };

    const redirectToPartner = (): void => {
        const returnUrl = partnerReturnUrl.value;
        if (!returnUrl) return;

        const url = new URL(returnUrl);
        url.searchParams.set("paymentType", "bank_transfer");
        if (checkoutStore.orderId) {
            url.searchParams.set("referenceId", checkoutStore.orderId);
        }
        window.location.href = url.toString();
    };

    const redirectToPartnerNow = (): void => {
        clearRedirectCountdown();
        redirectToPartner();
    };

    const startRedirectCountdown = (): void => {
        clearRedirectCountdown();
        redirectCountdown.value = 15;
        showRedirectCountdown.value = true;
        redirectInterval = setInterval(() => {
            redirectCountdown.value--;
            if (redirectCountdown.value === 0) {
                clearRedirectCountdown();
                redirectToPartner();
            }
        }, 1000);
    };

    watch(
        [isBankTransferPaid, partnerReturnUrl],
        ([paid, returnUrl]) => {
            if (paid && returnUrl && isPartnerLink()) {
                startRedirectCountdown();
            } else {
                clearRedirectCountdown();
            }
        },
        { immediate: true },
    );

    onScopeDispose(clearRedirectCountdown);

    // Shared
    const setSelectedPaymentMethod = (
        method: PaymentMethodType,
        cardProvider?: string,
    ): void => {
        resetInstantState();
        resetAdyenState();
        resetLloydsState();
        resetIfxState();
        resetVoltState();
        isEsendaPaymentSubmitted.value = false;
        selectedPaymentMethod.value = method;
        checkoutStore.triggerPayerInformationValidation = false;
        adyenStore.isPaymentActionInProgress = false;
        lloydsStore.isPaymentActionInProgress = false;
        if (cardProvider) selectedCardProvider.value = cardProvider;
    };

    const onPay = () => {
        isEsendaPaymentSubmitted.value = true;

        if (selectedPaymentMethod.value === PaymentMethodType.GLOBAL) {
            onSubmitIfxPayment();
        } else if (selectedPaymentMethod.value === PaymentMethodType.CARD) {
            checkoutStore.triggerPayerInformationValidation = true;

            if (selectedCardProvider.value === PaymentMethods.LLOYDS) {
                onSubmitLloydsPayment();
            } else {
                onSubmitAdyenPayment();
            }
        } else {
            onSubmitInstantPayment();
        }
    };

    return {
        // Instant
        instantBankInfo,
        isInstantBankSelected,
        isInstantPayButtonLoading,
        isInstantPayButtonDisabled,
        onSubmitInstantPayment,
        onInstantPayerValidationComplete,
        handleInvalidInstantNameState,

        // Adyen
        adyenFormInfo,
        isAdyenFormLoaded,
        isAdyenButtonLoading,
        isAdyenButtonVisible,
        isAdyenButtonDisabled,
        adyenButtonTooltipText,
        registerAdyenSubmitFn,
        onAdyenPayerValidationComplete,
        onSubmitAdyenPayment,
        handleInvalidAdyenAddressState,

        // Lloyds
        lloydsFormInfo,
        isLloydsFormLoaded,
        isLloydsButtonLoading,
        isLloydsButtonVisible,
        isLloydsButtonDisabled,
        lloydsButtonTooltipText,
        isLloydsPaymentSubmitted,
        registerLloydsFormInstance,
        onLloydsPayerValidationComplete,
        onSubmitLloydsPayment,
        handleInvalidLloydsAddressState,

        // IFX bank transfer
        isIfxInfoStep,
        isIfxTransferStep,
        isIfxConfirmStep,
        isIfxPayButtonDisabled,
        isIfxPayButtonVisible,
        showIfxPayLater,
        isIfxPayLaterConfirm,
        goToIfxTransferStep,
        goToIfxConfirmStep,
        onIfxPayLater,
        handleIfxBankDetailsLoaded,
        handleIfxQuoteExpiredState,
        onSubmitIfxPayment,

        // Volt bank transfer
        isVoltConfirmStep,
        isVoltPayLaterConfirm,
        isVoltPayerFormInvalid,
        goToVoltPayerStep,
        goToVoltDetailsStep,
        onVoltPaymentMade,
        onVoltPayLater,

        // Partner-link redirect
        redirectCountdown,
        showRedirectCountdown,
        redirectToPartnerNow,

        // Shared
        isPayButtonLoading,
        isPayButtonDisabled,
        isCardPaymentActionInProgress,
        isSelectedWalletOnlyMethod,
        isEsendaPaymentSubmitted,
        selectedPaymentMethod,
        selectedCardProvider,
        setSelectedPaymentMethod,
        onPay,
    };
};

export const useSubmitPayment = createSharedComposable(useSubmitPaymentBase);
